import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "./api";

const RestCtx = createContext(null);
// NOTE: the active outlet id is a NON-SENSITIVE user preference (which outlet
// the owner is currently editing). It is persisted in localStorage for a
// seamless multi-device experience. Auth tokens are held in an HttpOnly cookie,
// not in localStorage — see `/app/backend/server.py:set_auth_cookie`.
const STORAGE_KEY = "menumojo_active_restaurant";

export function RestaurantProvider({ children, user }) {
  const [restaurants, setRestaurants] = useState([]);
  const [activeId, setActiveIdState] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [loading, setLoading] = useState(false);

  const setActive = useCallback((id) => {
    setActiveIdState(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  const refresh = useCallback(async () => {
    if (!user || user.role !== "owner") {
      setRestaurants([]);
      return [];
    }
    setLoading(true);
    try {
      const { data } = await api.get("/restaurants");
      setRestaurants(data);
      if (data.length > 0) {
        const current = localStorage.getItem(STORAGE_KEY);
        const stillExists = data.find((r) => r.id === current);
        if (!stillExists) setActive(data[0].id);
      } else {
        setActive(null);
      }
      return data;
    } finally {
      setLoading(false);
    }
  }, [user, setActive]);

  useEffect(() => { refresh(); }, [user?.id, refresh]);

  const value = useMemo(() => {
    const active = restaurants.find((r) => r.id === activeId) || restaurants[0] || null;
    return {
      restaurants,
      active,
      activeId: active?.id || null,
      setActive,
      refresh,
      loading,
    };
  }, [restaurants, activeId, loading, setActive, refresh]);

  return <RestCtx.Provider value={value}>{children}</RestCtx.Provider>;
}

export const useRestaurant = () => useContext(RestCtx);
